import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AbilityServiceSignal } from '@casl/angular';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AuthorizationModule } from '@wilson/authorization';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import { FilterService } from '@wilson/filter';
import {
  RoleAction,
  RolePermissionSubject,
  TimelineShowStaysQueryParamKey,
} from '@wilson/interfaces';
import {
  ClearTimelineStays,
  SetAreStaysVisible,
  ShiftTimelineSettingsState,
  TimelineStaysSuggestionService,
} from '@wilson/shift-timeline/state';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { Subject, Subscription, debounceTime } from 'rxjs';
@Component({
  selector: 'wilson-timeline-render-stays-toggle',
  templateUrl: './timeline-render-stays-toggle.component.html',
  styleUrls: ['./timeline-render-stays-toggle.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    NzSwitchModule,
    FeatureFlagsModule,
    AuthorizationModule,
    NzTooltipModule,
  ],
})
export class TimelineRenderStaysToggleComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly filterService = inject(FilterService);
  private readonly activatedRoute = inject(ActivatedRoute);
  protected timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );

  @Input() disabled = false;

  RoleAction = RoleAction;
  RolePermissionSubject = RolePermissionSubject;
  private toggleStaysVisibilitySubject = new Subject<boolean>();
  private subscriptions = new Subscription();
  protected areStaysVisible$ = this.store.select(
    ShiftTimelineSettingsState.areStaysVisible,
  );
  protected can = inject(AbilityServiceSignal).can;

  ngOnInit(): void {
    const areStaysVisibleQueryValue = this.activatedRoute.snapshot.queryParams[
      TimelineShowStaysQueryParamKey
    ] as string;
    if (areStaysVisibleQueryValue) {
      const areStaysVisible =
        areStaysVisibleQueryValue?.toLowerCase() === 'true';
      this.saveToState(areStaysVisible);
    } else {
      const areStaysVisible = this.store.selectSnapshot(
        ShiftTimelineSettingsState.areStaysVisible,
      );
      this.saveToQueryParam(!!areStaysVisible);
    }

    this.subscriptions.add(
      this.toggleStaysVisibilitySubject
        .pipe(debounceTime(300))
        .subscribe((staysVisibility) => {
          this.performToggleOperation(staysVisibility);
        }),
    );
  }

  protected toggleStaysVisibility(staysVisibility: boolean): void {
    const updatedStaysVisibility = !staysVisibility;
    this.toggleStaysVisibilitySubject.next(updatedStaysVisibility);
  }

  private performToggleOperation(staysVisibility: boolean): void {
    this.saveToState(staysVisibility);
    this.saveToQueryParam(staysVisibility);
  }

  private saveToState(areStaysVisible: boolean): void {
    const isVisible = this.disabled ? false : areStaysVisible;
    this.store.dispatch(new SetAreStaysVisible(isVisible));

    if (!isVisible) {
      this.store.dispatch([new ClearTimelineStays()]);
      this.timelineStaysSuggestionService.resetRecommendedStaysInRangeMap();
    }
  }

  private saveToQueryParam(areStaysVisible: boolean): void {
    const isVisible = this.disabled ? false : areStaysVisible;
    this.filterService.saveValueInQueryParam(
      TimelineShowStaysQueryParamKey,
      isVisible.toString(),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
